import { useCallback } from 'react';
import { useSession } from '@ghq-abi/auth-client-lib/client';

import { mixPanel } from '~/shared/lib/mixpanel';

import { EEventsLabels } from './eventsEnum';

type AnalyticsPrimitive = string | number | boolean;
type AnalyticsValue = AnalyticsPrimitive | string[] | undefined;
type AnalyticsProperties = Record<string, AnalyticsValue>;

type BaseTrackParams = {
    action: string;
    component: string;
    event: EEventsLabels;
    page: string;
    properties?: AnalyticsProperties;
};

type FilterTrackParams = BaseTrackParams & {
    filterKey: string;
    filterValue?: AnalyticsValue;
};

type InputTrackParams = BaseTrackParams & {
    inputName: string;
    valueLength: number;
};

type TabTrackParams = BaseTrackParams & {
    tabId: string;
};

const sanitizeProperties = (properties: AnalyticsProperties = {}) =>
    Object.fromEntries(
        Object.entries(properties).filter(([, value]) => value !== undefined),
    ) as Record<string, AnalyticsPrimitive | string[]>;

export function useEvents() {
    const session = useSession();

    const fireBaseEvent = useCallback(
        ({ action, component, event, page, properties = {} }: BaseTrackParams) => {
            mixPanel.fireEvent({
                event,
                properties: sanitizeProperties({
                    action,
                    component,
                    page,
                    ...properties,
                }),
                session: (session?.data as any) ?? null,
            });
        },
        [session?.data],
    );

    const fireClickEvent = useCallback(
        (params: BaseTrackParams) => {
            fireBaseEvent(params);
        },
        [fireBaseEvent],
    );

    const fireToggleEvent = useCallback(
        (params: BaseTrackParams) => {
            fireBaseEvent(params);
        },
        [fireBaseEvent],
    );

    const fireEditEvent = useCallback(
        (params: BaseTrackParams) => {
            fireBaseEvent(params);
        },
        [fireBaseEvent],
    );

    const fireDownloadEvent = useCallback(
        (params: BaseTrackParams) => {
            fireBaseEvent(params);
        },
        [fireBaseEvent],
    );

    const fireFilterEvent = useCallback(
        ({ filterKey, filterValue, properties, ...params }: FilterTrackParams) => {
            fireBaseEvent({
                ...params,
                properties: {
                    ...properties,
                    filterKey,
                    filterValue,
                },
            });
        },
        [fireBaseEvent],
    );

    const fireInputEvent = useCallback(
        ({ inputName, valueLength, properties, ...params }: InputTrackParams) => {
            fireBaseEvent({
                ...params,
                properties: {
                    ...properties,
                    inputName,
                    valueLength,
                },
            });
        },
        [fireBaseEvent],
    );

    const fireTabEvent = useCallback(
        ({ tabId, properties, ...params }: TabTrackParams) => {
            fireBaseEvent({
                ...params,
                properties: {
                    ...properties,
                    tabId,
                },
            });
        },
        [fireBaseEvent],
    );

    return {
        fireClickEvent,
        fireDownloadEvent,
        fireEditEvent,
        fireFilterEvent,
        fireInputEvent,
        fireTabEvent,
        fireToggleEvent,
    };
}
